import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelServicePaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LocationIndexResource } from "../../../shared/types/location";
import { PermissionName } from "../../../shared/types/permission";
import { eventService } from "../shared/api/event/eventService";
import { locationService } from "../shared/api/locationService";
import { LocationCreateDrawer, LocationUpdateDrawer } from "./LocationDrawer";
import { LocationListContextMenu } from "./LocationListContextMenu";

export const LocationList: FC = () => {
	const { t } = useTranslation();

	const crudOperations = useLaravelCrudEntity(
		locationService,
		{
			entityName: t("Location"),
			messages: {
				create: t("Location was saved."),
				update: t("Location was saved."),
				delete: t("Location was deleted."),
			},
		},
		[],
		[eventService.indexKey],
	);

	const dataListColumns: IDrawerDataListColumn<LocationIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<LocationListContextMenu
						locationId={id}
						handleEdit={openDrawer}
						handleDelete={() => crudOperations.deleteEntity(id)}
					/>
				),
			},
		],
		[crudOperations, t],
	);

	const canCreate = useHasPermission<PermissionName>(["location.create"]);
	const canUpdate = useHasPermission<PermissionName>(["location.update"]);

	return (
		<PaginatedDrawerAutoDataList
			canUpdate={canUpdate}
			canCreate={canCreate}
			initialSearch={{
				search: "",
				searchFields: [{ name: "name", label: t("Name") }],
			}}
			translations={{
				title: t("Locations"),
				empty: t("No locations available"),
			}}
			renderCreateDrawer={(drawerProps) => (
				<LocationCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<LocationUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
			columns={dataListColumns}
			listFetchConfig={laravelServicePaginatedListFetchConfig(locationService)}
		/>
	);
};
